import React from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import css from './SectionClientReviews.module.css';
import config from '../../../config/config';
import { CountUp } from '../../../components';
import { t } from '../../../util/messages';

/**
 * Client reviews.
 *
 * A card grid rather than a carousel: testimonials are scanned, not paged
 * through, and every quote being visible at once is itself the argument.
 * The first review renders as a large dark card to anchor the section.
 */

const QuoteMark = () => (
  <svg
    className={css.quoteMark}
    width="34"
    height="26"
    viewBox="0 0 34 26"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M0 26V14.6C0 6.6 4.6 1.2 13 0l1.4 4.2C9.8 5.6 7.4 8.4 7.4 12h5.8v14H0zm19.4 0V14.6C19.4 6.6 24 1.2 32.4 0l1.4 4.2c-4.6 1.4-7 4.2-7 7.8h5.8v14H19.4z"
      fill="currentColor"
    />
  </svg>
);

const Stars = ({ count }) => (
  <span className={css.stars} aria-label={`${count} out of 5`}>
    {Array.from({ length: count }, (_, index) => (
      <svg key={index} width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 .8l2.2 4.6 5 .7-3.6 3.5.9 5L8 12.2l-4.5 2.4.9-5L.8 6.1l5-.7L8 .8z" />
      </svg>
    ))}
  </span>
);

const ReviewCard = ({ review, delay }) => {
  const isFeatured = Boolean(review.featured);

  return (
    <figure
      className={classNames(css.review, { [css.reviewFeatured]: isFeatured })}
      data-reveal
      data-reveal-delay={delay}
    >
      {isFeatured ? <QuoteMark /> : review.rating ? <Stars count={review.rating} /> : null}

      <blockquote className={css.quote}>{review.excerpt || review.review}</blockquote>

      <figcaption className={css.attribution}>
        <img
          className={css.clientPhoto}
          src={review.clientPhoto}
          alt=""
          width="46"
          height="46"
          loading="lazy"
        />
        <span>
          <span className={css.clientName}>{review.clientName}</span>
          <span className={css.clientRole}>{review.clientPosition}</span>
        </span>
      </figcaption>
    </figure>
  );
};

const SectionClientReviews = ({ rootClassName, className }) => {
  const classes = classNames(rootClassName || css.root, className);
  const reviews = config.configReviews;

  return (
    <section id="reviews" className={classes} aria-label="Client reviews">
      <div className={css.content}>
        <header className={css.header} data-reveal>
          <div>
            <span className={css.tag}>
              <i aria-hidden="true" />
              {t('SectionClientReviews.eyebrow')}
            </span>
            <h2 className={css.title}>{t('SectionClientReviews.heading')}</h2>
          </div>
          <p className={css.description}>{t('SectionClientReviews.description')}</p>
        </header>

        <div className={css.reviews}>
          {reviews.map((review, index) => (
            <ReviewCard key={review.id} review={review} delay={(index % 2) * 80} />
          ))}

          {/*
            Closing tile. The featured card spans two columns, so four
            reviews leave one slot empty in the three-column grid; this fills
            it with a summary rather than a hole.
          */}
          <div className={css.summary} data-reveal data-reveal-delay="80">
            <CountUp
              className={css.summaryValue}
              value={t('SectionStatsAndProcess.clientSatisfactionPercentage')}
            />
            <p className={css.summaryText}>{t('SectionClientReviews.summary')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

SectionClientReviews.propTypes = {
  rootClassName: string,
  className: string,
};

export default React.memo(SectionClientReviews);
